import React, { useState, useEffect } from 'react';
import {
    Terminal,
    Copy,
    Check,
    ExternalLink,
    ShieldAlert,
    Search,
    Scale,
    Activity,
    ZoomIn,
    BookOpen
} from 'lucide-react';

// --- Standardized Components ---

const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-dark-800 shadow-glow rounded-lg mb-6 border border-gray-200 dark:border-dark-600 ${className}`}>
        {children}
    </div>
);

const SectionHeader = ({ id, number, title }) => (
    <div className="flex items-center gap-4 mb-4" id={id}>
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-mint-100 text-mint-700 dark:bg-mint-900/30 dark:text-mint-400 font-bold text-sm">
            {String(number).padStart(2, '0')}
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
    </div>
);

const ChevronNav = ({ activePhase }) => {
    const phases = [
        { id: 1, label: 'Analysis' },
        { id: 2, label: 'Evidence' },
        { id: 3, label: 'Mitigation' },
        { id: 4, label: 'Takedown' },
        { id: 5, label: 'Legal' },
        { id: 6, label: 'Monitoring' },
    ];

    return (
        <nav className="mb-8 overflow-x-auto pb-2">
            <div className="flex space-x-1 min-w-max border-b border-gray-200 dark:border-dark-600">
                {phases.map((phase) => (
                    <a
                        key={phase.id}
                        href={`#phase-${phase.id}`}
                        className={`
                            px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors
                            ${activePhase === phase.id
                                ? 'border-mint-500 text-mint-600 dark:text-mint-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                            }
                        `}
                    >
                        {phase.id}. {phase.label}
                    </a>
                ))}
            </div>
        </nav>
    );
};

const WarningBlock = () => {
    return (
        <div className="rounded-md bg-red-50 dark:bg-red-900/10 p-4 border border-red-200 dark:border-red-800 mb-6 shadow-glow">
            <div className="flex">
                <div className="flex-shrink-0">
                    <ShieldAlert className="h-5 w-5 text-red-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                        CRITICAL WARNING
                    </h3>
                    <div className="mt-2 text-sm text-red-700 dark:text-red-400">
                        <p>
                            Never visit a suspicious URL directly from a standard workstation. Use a secure sandbox or isolated virtual machine (e.g., Browserling, Joe Sandbox).
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CommandBlock = ({ command, label }) => {
    const [copied, setCopied] = useState(false);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(command);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="my-2 rounded-md bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-700 overflow-hidden shadow-glow">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-100 dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700">
                <div className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    <Terminal className="w-3 h-3" />
                    <span>{label || 'Terminal'}</span>
                </div>
                <button
                    onClick={copyToClipboard}
                    className="flex items-center gap-1 text-xs text-mint-600 hover:text-mint-700 dark:text-mint-400 transition-colors"
                >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </div>
            <div className="p-3 font-mono text-sm text-gray-800 dark:text-gray-300 overflow-x-auto">
                $ {command}
            </div>
        </div>
    );
};

const DecisionMatrix = () => {
    return (
        <div className="overflow-hidden shadow-glow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-dark-600">
                <thead className="bg-gray-50 dark:bg-dark-700">
                    <tr>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Threat Type</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Primary Action</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Secondary Action</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-600 bg-white dark:bg-dark-800">
                    <tr className="hover:bg-gray-50 dark:hover:bg-dark-700/50">
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-white font-medium">Parking Page (Ads)</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">Monitor (Passive)</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">Potential Acquisition</td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-dark-700/50">
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-white font-medium">Redirect to Competitor</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">Legal (Cease & Desist)</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">UDRP Complaint</td>
                    </tr>
                    <tr className="bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500">
                        <td className="whitespace-nowrap px-3 py-4 text-sm font-bold text-red-700 dark:text-red-300 flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4" />
                            Phishing / Malware
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm font-bold text-red-700 dark:text-red-300">
                            IMMEDIATE ABUSE TAKEDOWN
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-red-600 dark:text-red-400">Safe Browsing Blacklist</td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-dark-700/50">
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-white font-medium">Active MX Record</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">Email Gateway Block</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">Registrar Takedown</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

const TemplateGenerator = () => {
    const [formData, setFormData] = useState({
        domain: '',
        registrar: '',
        hostingProvider: '',
        brandName: 'TypoGuard',
        hostingIP: ''
    });
    const [copied, setCopied] = useState(false);

    const generateEmail = () => {
        return `Subject: URGENT: Phishing and Trademark Infringement Report - ${formData.domain || '[Suspicious Domain]'}

To: abuse@${formData.registrar || '[registrar].com'}, security@${formData.hostingProvider || '[hosting-provider].com'}

Body:

To the Abuse Department,

We have identified a domain, ${formData.domain || '[Suspicious Domain]'}, which is registered and/or hosted on your network. This domain is currently being used for illegal activities (Typosquatting/Phishing) targeting our organization, ${formData.brandName}.

Incident Details:
* Malicious URL: http://${formData.domain || '[Suspicious Domain]'}
* Targeted Brand: ${formData.brandName}
* Hosting IP: ${formData.hostingIP || '[IP Address]'}
* Violation: This website is a fraudulent copy of our official site intended to steal user credentials.

Evidence:
Please find the attached screenshots and analysis demonstrating the infringement.

This activity violates your Terms of Service regarding illegal usage, fraud, and trademark infringement. We request the immediate suspension of this domain and the associated hosting account.

Please confirm receipt of this report and inform us of the actions taken.

Sincerely,

SOC Team
${formData.brandName}`;
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(generateEmail());
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 my-6">
            <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Input Parameters</h3>
                <div className="space-y-3">
                    {['domain', 'registrar', 'hostingProvider', 'brandName', 'hostingIP'].map((field) => (
                        <div key={field}>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                                {field.replace(/([A-Z])/g, ' $1').trim()}
                            </label>
                            <input
                                type="text"
                                value={formData[field]}
                                onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                                placeholder={`Enter ${field}...`}
                                className="block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-mint-500 focus:ring-mint-500 sm:text-sm dark:bg-dark-700 dark:text-white"
                            />
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Preview</h3>
                    <button
                        onClick={handleCopy}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                    >
                        {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                        {copied ? 'Copied' : 'Copy Email'}
                    </button>
                </div>
                <div className="relative flex-1 rounded-md bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-700 p-4 overflow-auto max-h-[500px]">
                    <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 dark:text-gray-300 leading-relaxed">
                        {generateEmail()}
                    </pre>
                </div>
            </div>
        </div>
    );
};

// --- Main Page Component ---

const SOP = () => {
    const [activePhase, setActivePhase] = useState(1);
    const [isZoomed, setIsZoomed] = useState(false);

    // Scroll spy to update active nav
    useEffect(() => {
        const handleScroll = () => {
            const phases = [1, 2, 3, 4, 5, 6];
            for (const phase of phases) {
                const element = document.getElementById(`phase-${phase}`);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    // Adjust offset for better detection
                    if (rect.top >= 0 && rect.top <= 500) {
                        setActivePhase(phase);
                        break;
                    }
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
            {/* Standard Dashboard Layout Wrapper */}
            <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">

                {/* 1. Zoomable Reference Image Sidebar/Modal (Simplified) */}
                <div className={`
                    fixed z-40 transition-all duration-500 ease-in-out bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 shadow-lg
                    ${isZoomed ? 'inset-4 md:inset-20 flex items-center justify-center p-4 rounded-xl' : 'bottom-8 right-8 w-16 h-16 rounded-full hover:scale-105 cursor-pointer flex items-center justify-center'}
                `}>
                    <div className="relative w-full h-full group" onClick={() => setIsZoomed(!isZoomed)}>
                        {isZoomed ? (
                            <img
                                src="/TakeDown.png"
                                alt="SOP Reference Infographic"
                                className="w-full h-full object-contain"
                            />
                        ) : (
                            <BookOpen className="w-8 h-8 text-mint-600" />
                        )}
                        {isZoomed && (
                            <button
                                className="absolute top-2 right-2 p-2 bg-white dark:bg-dark-800 rounded-full shadow-md text-gray-500 hover:text-gray-700 dark:text-gray-400"
                                onClick={(e) => { e.stopPropagation(); setIsZoomed(false); }}
                            >
                                <span className="sr-only">Close</span>
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                {/* Header */}
                <div className="md:flex md:items-center md:justify-between px-4 sm:px-0 mb-6">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-mint-100 text-mint-800 dark:bg-mint-900/30 dark:text-mint-400">
                                SEC-OPS-05
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">v1.0</span>
                        </div>
                        <h1 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:truncate sm:text-3xl sm:tracking-tight">
                            Typosquatting Detection & Takedown
                        </h1>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-3xl">
                            Standardized workflow for identifying, analyzing, and mitigating illegal domain names infringing on intellectual property.
                        </p>
                    </div>
                </div>

                <div className="px-4 sm:px-0">
                    <ChevronNav activePhase={activePhase} />
                </div>

                <div className="space-y-8 px-4 sm:px-0">
                    {/* Phase 1 */}
                    <section id="phase-1" className="scroll-mt-6">
                        <Card className="p-6">
                            <SectionHeader id="phase-1-header" number={1} title="Analysis & Qualification" />
                            <WarningBlock />

                            <div className="grid md:grid-cols-2 gap-8 mt-6">
                                <div>
                                    <h3 className="text-sm font-medium text-gray-900 dark:text-white uppercase tracking-wide mb-3">Visual Inspection Criteria</h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start">
                                            <Search className="h-5 w-5 text-mint-500 mr-2" />
                                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                                <strong className="font-medium text-gray-900 dark:text-white">Clone/Phishing:</strong> Exact copy of the site logic or branding.
                                            </span>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="h-5 w-5 flex items-center justify-center mr-2">
                                                <div className="h-1.5 w-1.5 rounded-full bg-gray-400"></div>
                                            </div>
                                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                                <strong className="font-medium text-gray-900 dark:text-white">Parking Page:</strong> Generic ads, "Domain for Sale" landers.
                                            </span>
                                        </li>
                                        <li className="flex items-start">
                                            <ShieldAlert className="h-5 w-5 text-red-500 mr-2" />
                                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                                <strong className="font-medium text-gray-900 dark:text-white">Timer Redirects:</strong> Immediate redirect to malware/spam.
                                            </span>
                                        </li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-sm font-medium text-gray-900 dark:text-white uppercase tracking-wide mb-3">Technical Command Center</h3>
                                    <CommandBlock command="dig +short MX suspicious-domain.com" label="Check MX Records" />
                                    <p className="text-xs text-gray-500 mt-2">
                                        * Presence of MX records indicates high risk of CEO Fraud / BEC attacks.
                                    </p>
                                </div>
                            </div>
                        </Card>
                    </section>

                    {/* Phase 2 */}
                    <section id="phase-2" className="scroll-mt-6">
                        <Card className="p-6">
                            <SectionHeader id="phase-2-header" number={2} title="Evidence Gathering" />

                            <div className="grid md:grid-cols-3 gap-6 mt-6">
                                {[
                                    { icon: Search, title: 'Screenshots', desc: 'Capture full-page with system date/time visible.' },
                                    { icon: Activity, title: 'Source Code', desc: 'Save HTML. Check for asset hotlinking.' },
                                    { icon: ExternalLink, title: 'Archiving', desc: 'Submit to Wayback Machine or Urlscan.io.' },
                                ].map((item, i) => (
                                    <div key={i} className="flex flex-col items-center text-center p-4 bg-gray-50 dark:bg-dark-900/50 rounded-lg border border-gray-100 dark:border-dark-700 shadow-glow">
                                        <div className="p-2 bg-white dark:bg-dark-800 rounded-lg shadow-sm mb-3">
                                            <item.icon className="w-6 h-6 text-mint-600" />
                                        </div>
                                        <h4 className="font-medium text-gray-900 dark:text-white">{item.title}</h4>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </section>

                    {/* Phase 3 & 4 */}
                    <section id="phase-3" className="scroll-mt-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <Card className="p-6">
                                <SectionHeader id="phase-3-header" number={3} title="Immediate Mitigation" />
                                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                                    Protect internal infrastructure and employees immediately upon confirmation.
                                </p>
                                <div className="flex flex-col space-y-2">
                                    <div className="flex items-center p-3 bg-gray-50 dark:bg-dark-900/50 rounded-md border border-gray-100 dark:border-dark-700 shadow-glow">
                                        <Check className="h-4 w-4 text-green-500 mr-3" />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Block on Corporate Firewall/Proxy</span>
                                    </div>
                                    <div className="flex items-center p-3 bg-gray-50 dark:bg-dark-900/50 rounded-md border border-gray-100 dark:border-dark-700 shadow-glow">
                                        <Check className="h-4 w-4 text-green-500 mr-3" />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Block domain on Email Gateway</span>
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6" id="phase-4">
                                <SectionHeader id="phase-4-header" number={4} title="Takedown Procedure" />
                                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                                    Report to external authorities.
                                </p>
                                <div className="space-y-3">
                                    {[
                                        { name: 'Google Safe Browsing', url: 'https://safebrowsing.google.com/safebrowsing/report_phish/' },
                                        { name: 'Microsoft SmartScreen', url: 'https://www.microsoft.com/en-us/wdsi/support/report-unsafe-site' },
                                        { name: 'Netcraft', url: 'https://report.netcraft.com/' }
                                    ].map((service) => (
                                        <a
                                            key={service.name}
                                            href={service.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center justify-between p-3 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-md hover:border-mint-500 hover:shadow-glow transition-all group"
                                        >
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-mint-600 dark:group-hover:text-mint-400">{service.name}</span>
                                            <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-mint-500" />
                                        </a>
                                    ))}
                                </div>
                            </Card>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <Scale className="w-5 h-5 text-gray-500" />
                            Dynamic Decision Matrix
                        </h2>
                        <Card className="overflow-hidden">
                            <DecisionMatrix />
                        </Card>
                    </section>

                    {/* Phase 5 */}
                    <section id="phase-5" className="scroll-mt-6">
                        <Card className="p-6">
                            <SectionHeader id="phase-5-header" number={5} title="Legal Escalation" />
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="p-4 bg-gray-50 dark:bg-dark-900/50 rounded-lg border border-gray-200 dark:border-dark-700 shadow-glow">
                                    <strong className="block text-sm font-semibold text-gray-900 dark:text-white mb-1">1. Cease & Desist</strong>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Formal letter to Registrant or Privacy Proxy service requesting immediate takedown.</p>
                                </div>
                                <div className="p-4 bg-gray-50 dark:bg-dark-900/50 rounded-lg border border-gray-200 dark:border-dark-700 shadow-glow">
                                    <strong className="block text-sm font-semibold text-gray-900 dark:text-white mb-1">2. UDRP Filing (WIPO)</strong>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Forced transfer of ownership if trademark is held. Costs apply but outcome is binding.</p>
                                </div>
                            </div>
                        </Card>
                    </section>

                    {/* Phase 6 */}
                    <section id="phase-6" className="scroll-mt-6">
                        <Card className="p-6">
                            <SectionHeader id="phase-6-header" number={6} title="Monitoring & Closure" />
                            <div className="flex items-start bg-mint-50 dark:bg-mint-900/10 p-4 rounded-md border border-mint-100 dark:border-mint-900/30 shadow-glow">
                                <Check className="w-5 h-5 text-mint-600 mt-0.5 mr-3" />
                                <div>
                                    <h4 className="font-medium text-mint-900 dark:text-mint-400 text-sm">Closure Criteria</h4>
                                    <p className="text-sm text-mint-800 dark:text-mint-300 mt-1">
                                        Close ticket ONLY when DNS resolves to <code className="bg-white/50 px-1 py-0.5 rounded text-xs font-mono">NXDOMAIN</code> or content is permanently removed. Continue monitoring for 14 days.
                                    </p>
                                </div>
                            </div>
                        </Card>
                    </section>

                    {/* Appendix A: Template Generator */}
                    <section>
                        <Card className="p-6">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Appendix A: Live Abuse Report Generator</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Generates a standardized takedown email based on your findings.</p>
                            <TemplateGenerator />
                        </Card>
                    </section>

                </div>
            </div>
        </div >
    );
};

export default SOP;
